import {
  makeObservable,
  observable,
  action,
  computed,
  runInAction
} from 'mobx';

import { MESSAGES_LIMIT } from 'widget/consts/api';
import type { IMessage, SendAttachment } from 'widget/types/messages';
import { MessageStatus, TextFormat } from 'widget/types/messages';
import { generateId } from 'widget/utils/generateId';
import type { MediaAttachment } from 'widget/types/attachments';

import { MessageKind, type MessageHistoryItem } from '../ApiStore/types';
import type { RootStore } from '../RootStore';
import { WsMessageKind, WsPublicationData } from '../WebSocketStore/types';

type PrivateFields =
  | '_messages'
  | '_isTyping'
  | '_isLoadingHistory'
  | '_hasMore';

export class MessagesStore {
  private _rootStore: RootStore;

  private _messages: IMessage[] = [];
  private _isTyping = false;
  private _isLoadingHistory = false;
  private _hasMore = true;

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;

    makeObservable<MessagesStore, PrivateFields>(this, {
      _messages: observable.ref,
      _isTyping: observable,
      _isLoadingHistory: observable,
      _hasMore: observable,

      messages: computed,
      isTyping: computed,
      isLoadingHistory: computed,
      hasMore: computed,

      loadHistory: action,
      sendMessage: action,
      retryMessage: action,
      handleWsMessage: action,
      setTyping: action
    });
  }

  get messages(): IMessage[] {
    return this._messages;
  }

  get isTyping(): boolean {
    return this._isTyping;
  }

  get isLoadingHistory(): boolean {
    return this._isLoadingHistory;
  }

  get hasMore(): boolean {
    return this._hasMore;
  }

  loadHistory = async (): Promise<void> => {
    if (this._isLoadingHistory || !this._hasMore) {
      return;
    }

    runInAction(() => {
      this._isLoadingHistory = true;
    });

    try {
      const fromId = this._messages[0]?.id;

      const response = await this._rootStore.apiStore.getMessagesHistory({
        limit: MESSAGES_LIMIT,
        from_id: fromId
      });

      if (response.isError || !response.data) {
        return;
      }

      const rawMessages = response.data.data.messages;

      const historyMessages = rawMessages
        .map((message) => this._safeConvertToMessage(message))
        .filter(Boolean) as IMessage[];

      runInAction(() => {
        this._messages = [...historyMessages, ...this._messages];
        this._hasMore = rawMessages.length === MESSAGES_LIMIT;
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load history:', error);
    } finally {
      runInAction(() => {
        this._isLoadingHistory = false;
      });
    }
  };

  sendMessage = async (
    text: string,
    attachments?: SendAttachment[]
  ): Promise<void> => {
    if (!text.trim() && (!attachments || attachments.length === 0)) {
      return;
    }

    const tempId = this._createTempId();

    const tempAttachments: MediaAttachment[] = attachments
      ? attachments.map((a) => ({
          id: a.id,
          filename: a.filename,
          size: a.size,
          type: a.type,
          url: a.url,
          preview_url: a.preview_url
        }))
      : [];

    const tempMessage: IMessage = {
      id: tempId,
      text,
      date: new Date(),
      isBot: false,
      status: MessageStatus.PENDING,
      attachments: tempAttachments
    };

    runInAction(() => {
      this._messages = [...this._messages, tempMessage];
    });

    // Извлечь IDs для API
    const attachmentIds = attachments?.map((a) => a.id);
    const response = await this._rootStore.apiStore.sendMessage(
      text,
      attachmentIds
    );

    if (response.isError) {
      this._updateMessage(tempId, { status: MessageStatus.ERROR });
      return;
    }

    const newId = response.data?.data._id;
    if (!newId) {
      return;
    }

    this._updateMessage(tempId, {
      id: newId,
      status: MessageStatus.SENT
    });
  };

  retryMessage = async (messageId: string): Promise<void> => {
    const message = this._getMessageById(messageId);
    if (!message) {
      return;
    }

    this._updateMessage(messageId, { status: MessageStatus.PENDING });

    const attachmentIds = message.attachments
      ?.map((a) => a.id)
      .filter((id): id is string => Boolean(id));

    const response = await this._rootStore.apiStore.sendMessage(
      message.text,
      attachmentIds && attachmentIds.length > 0 ? attachmentIds : undefined
    );

    if (response.isError) {
      this._updateMessage(messageId, { status: MessageStatus.ERROR });
      return;
    }

    const newId = response.data?.data._id;
    if (!newId) {
      return;
    }

    this._updateMessage(messageId, { id: newId, status: MessageStatus.SENT });
  };

  handleWsMessage = (data: WsPublicationData | MessageHistoryItem): void => {
    try {
      const messageData: MessageHistoryItem =
        'kind' in data && data.kind === WsMessageKind.CHAT_MESSAGE
          ? (data.data as MessageHistoryItem)
          : data;

      const message = this._safeConvertToMessage(messageData);
      if (!message) {
        return;
      }

      if (message.isBot) {
        this._messages = [...this._messages, message];
        this._isTyping = false;
        return;
      }

      runInAction(() => {
        let index = this._findMessageIndex(message.id);

        if (index === -1) {
          const incomingAttachments = message.attachments ?? [];
          const incomingIds = incomingAttachments.map(
            (attachment) => attachment.id ?? ''
          );
          index = this._messages.findIndex(
            (mess) =>
              mess.status === MessageStatus.PENDING &&
              mess.text === message.text &&
              (mess.attachments ?? []).length === incomingAttachments.length &&
              (mess.attachments ?? []).every((att, i) => {
                const localId = att.id ?? '';
                const wsId = incomingIds[i];
                if (localId && wsId) {
                  return localId === wsId;
                }
                return att.filename === incomingAttachments[i]?.filename;
              })
          );
        }

        if (index !== -1) {
          this._messages = this._replaceMessage(index, {
            id: message.id,
            status: MessageStatus.SENT
          });
          return;
        }

        this._messages = [...this._messages, message];
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error, data);
    }
  };

  setTyping = (isTyping: boolean): void => {
    this._isTyping = isTyping;
  };

  private _findMessageIndex(id: string): number {
    return this._messages.findIndex((msg) => msg.id === id);
  }

  private _getMessageById(id: string): IMessage | undefined {
    return this._messages.find((msg) => msg.id === id);
  }

  private _updateMessage(id: string, patch: Partial<IMessage>): void {
    runInAction(() => {
      const index = this._findMessageIndex(id);
      if (index === -1) {
        return;
      }

      this._messages = this._replaceMessage(index, patch);
    });
  }

  private _replaceMessage(index: number, patch: Partial<IMessage>): IMessage[] {
    const updated = [...this._messages];
    updated[index] = {
      ...updated[index],
      ...patch
    };
    return updated;
  }

  private _createTempId(): string {
    return generateId();
  }

  private _safeConvertToMessage(item: MessageHistoryItem): IMessage | null {
    if (!item?.date_created || !item?.kind) {
      return null;
    }

    const isWelcome = item.id === null;

    return {
      id: item.id ?? this._createTempId(),
      text: item.text ?? '',
      date: new Date(item.date_created),
      isBot: item.kind === MessageKind.BOT,
      attachments: item.attachments ?? [],
      status: MessageStatus.SENT,
      isWelcome: isWelcome,
      textFormat: item.extra?.text_format as TextFormat | undefined
    };
  }

  destroy = () => {
    this._messages = [];
  };
}
